import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcceptInvitationStepBase } from '../../shared/accept-invitation-step-base/accept-invitation-step-base';
import { PlaidService } from '../../services/plaid.service';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';

// Make TypeScript understand window.Plaid exists
declare global {
  interface Window {
    Plaid: any;
  }
}

@Component({
  selector: 'app-link-bank-account',
  templateUrl: './link-bank-account.component.html',
  styleUrl: './link-bank-account.component.scss'
})
export class LinkBankAccountComponent extends AcceptInvitationStepBase<any> implements OnInit {
  // Form configuration
  public override readonly form: FormGroup = inject(FormBuilder).group({
    bankConnected: [false], // Hidden field to persist bank connection status
    bankNotInPlaid: [false] // New field for banks not in Plaid
  });
  
  // Define component properties
  public override readonly nextUrl = '../certifications';
  public readonly commService: InvitationCommunicationService = inject(InvitationCommunicationService);
  
  // Dynamic property to determine the previous step based on the ownership disclosure answer
  public override get previousUrl(): string {
    if (this.commService.role === 'supplier') {
      // If the user is a supplier, go back to business-location
      return '../business-location';
    } else if (this.commService.role === 'borrower') {
      // If the user is a borrower, handle based on ownership disclosure

      // If noIndividualOver25 is "no", skip additional-owners and go directly to ownership-disclosure
      const ownershipDisclosure = this.dataStorageService.getItem('ownershipDisclosureForm') as { noIndividualOver25?: string } | null;
      return ownershipDisclosure?.noIndividualOver25 === 'no' ? '../ownership-disclosure' : '../additional-owners';
    } else {
      // Invalid role, default to business-location
      console.error("Invalid user role:", this.commService.role);
      return '../business-location';
    }
  }
  
  protected override readonly storageKey = 'linkBankAccountForm';
  
  // Plaid related properties
  private plaidHandler: any;
  private _bankConnected = false;
  public plaidLinkActive = false; // Track when Plaid Link modal is active
  
  // Services
  private readonly plaidService = inject(PlaidService);
  private readonly snackBar = inject(MatSnackBar);
  
  /**
   * Getter for bankConnected property
   * Uses getter/setter to synchronize the property with the form field
   */
  public get bankConnected(): boolean {
    return this._bankConnected;
  }
  
  /**
   * Setter for bankConnected property
   * Updates both the private property and the form field
   */
  public set bankConnected(value: boolean) {
    this._bankConnected = value;
    this.form.get('bankConnected')?.setValue(value);
    // Save the bank connection state as soon as it is established.
    this.persistFormData();
  }
  
  /**
   * Initialize component and synchronize saved state
   */
  protected override afterFormLoaded(): void {
    // Reset bankConnected to ensure it starts as false unless loaded from the draft
    this._bankConnected = false;

    // Synchronize the bankConnected property with the (now-loaded) form value
    const savedValue = this.form.get('bankConnected')?.value;
    if (savedValue !== undefined) {
      this._bankConnected = savedValue;
    }
  }
  
  /**
   * Initialize Plaid Link flow
   * Creates a link token via the backend API and opens Plaid Link interface
   */
  public async initPlaidLink(): Promise<void> {
    // Uncheck bank not in Plaid checkbox
    this.form.get('bankNotInPlaid')?.setValue(false);

    // Wait for draft hydrate + a successful flush so a server-minted GUID exists.
    const applicationGuid = await this.dataStorageService.ensureApplicationGuid();
    if (!applicationGuid) {
      this.snackBar.open('Error: Application ID not found. Please try again.', 'Close', { duration: 5000 });
      return;
    }

    // Set flag to indicate Plaid Link process is active
    this.plaidLinkActive = true;
    
    this.plaidService.createLinkToken(applicationGuid).subscribe({
      next: (resp) => {
        // Create the Plaid Link handler
        this.plaidHandler = window.Plaid.create({
          token: resp.link_token,
          onSuccess: (public_token: string, metadata: any) => {
            // Once user successfully authenticates with their bank
            // Exchange public token for access token via backend
            this.onSuccess(public_token);
          },
          onExit: (err: any) => {
            // Reset flag when user exits Plaid Link interface
            this.plaidLinkActive = false;
            
            if (err != null) {
              console.error('Plaid Link encountered an error', err);
              this.snackBar.open('Error connecting to bank', 'Close', { duration: 5000 });
            }
          },
          onEvent: () => {
            // Optional event tracking
          },
          forceIframe: true, // Force iframe for better in-app experience
        });

        // Open Plaid Link interface
        this.plaidHandler.open();
      },
      error: (err) => {
        console.error('Error creating link token', err);
        this.plaidLinkActive = false; // Reset active state on error
        this.snackBar.open('Error initializing bank connection', 'Close', { duration: 5000 });
      }
    });
  }

  public async onSuccess(public_token: string): Promise<void> {
    const applicationGuid = await this.dataStorageService.ensureApplicationGuid();
    if (!applicationGuid) {
      this.snackBar.open('Error: Application ID not found. Please try again.', 'Close', { duration: 5000 });
      return;
    }

    this.plaidService.exchangePublicToken(public_token, applicationGuid).subscribe({
      next: () => {
        // Set flag to show success message
        this.bankConnected = true;
        this.plaidLinkActive = false; // Reset active state
        this.snackBar.open('Bank account successfully connected!', 'Close', { duration: 5000 });
      },
      error: (err) => {
        console.error('Error exchanging public token', err);
        this.plaidLinkActive = false; // Reset active state on error
        this.snackBar.open('Error connecting bank account', 'Close', { duration: 5000 });
      }
    });
  }
}